import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { gameDetails } from '../App';

interface GameSettings {
  visible: boolean;
  order: number;
}

const Settings: React.FC = () => {
  const { t } = useLanguage();
  const [gameSettings, setGameSettings] = useState<Record<string, GameSettings>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    // Load settings from localStorage
    const savedSettings = localStorage.getItem('gameSettings');
    if (savedSettings) {
      setGameSettings(JSON.parse(savedSettings));
    } else {
      // Initialize with default settings
      const defaultSettings: Record<string, GameSettings> = {};
      Object.keys(gameDetails).forEach((gameKey, index) => {
        defaultSettings[gameKey] = {
          visible: true,
          order: index
        };
      });
      setGameSettings(defaultSettings);
    }
  }, []);

  const handleVisibilityChange = (gameKey: string) => {
    setGameSettings(prev => ({
      ...prev,
      [gameKey]: {
        ...prev[gameKey],
        visible: !prev[gameKey].visible
      }
    }));
  };

  const handleOrderChange = (gameKey: string, newOrder: number) => {
    const currentOrder = gameSettings[gameKey].order;
    
    // Update all affected games
    const updatedSettings = { ...gameSettings };
    Object.keys(updatedSettings).forEach(key => {
      if (newOrder > currentOrder) {
        // Moving down
        if (updatedSettings[key].order > currentOrder && updatedSettings[key].order <= newOrder) {
          updatedSettings[key].order--;
        }
      } else {
        // Moving up
        if (updatedSettings[key].order >= newOrder && updatedSettings[key].order < currentOrder) {
          updatedSettings[key].order++;
        }
      }
    });
    
    updatedSettings[gameKey].order = newOrder;
    setGameSettings(updatedSettings);
  };

  const saveSettings = () => {
    setIsSaving(true);
    localStorage.setItem('gameSettings', JSON.stringify(gameSettings));
    setTimeout(() => {
      setIsSaving(false);
    }, 1000);
  };

  const sortedGames = Object.entries(gameSettings)
    .sort(([, a], [, b]) => a.order - b.order);

  return (
    <div className="h-full bg-gray-50">
      <main className="pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">{t("settings")}</h1>
            <button
              onClick={saveSettings}
              disabled={isSaving}
              className={`px-4 py-2 rounded-lg text-white font-medium ${
                isSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'
              }`}
            >
              {isSaving ? t("saving") : t("save_settings")}
            </button>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">{t("game_visibility")}</h2>
            <div className="space-y-4">
              {sortedGames.map(([gameKey, settings]) => (
                <div key={gameKey} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <img 
                      src={gameDetails[gameKey].logo} 
                      alt={gameDetails[gameKey].name} 
                      className="w-10 h-10 rounded-full"
                    />
                    <span className="font-medium">{gameDetails[gameKey].name}</span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleOrderChange(gameKey, settings.order - 1)}
                        disabled={settings.order === 0}
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => handleOrderChange(gameKey, settings.order + 1)}
                        disabled={settings.order === Object.keys(gameSettings).length - 1}
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                      >
                        ↓
                      </button>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.visible}
                        onChange={() => handleVisibilityChange(gameKey)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Settings; 